import { useUIStore } from '@/stores/uiStore';
import { useGameStore } from '@/stores/gameStore';
import { Team } from '@/types/player';
import './RoundResultModal.css';

/**
 * ラウンド結果モーダルコンポーネント
 * ラウンド終了時のダメージ計算結果を表示し、次ラウンドへ進む
 *
 * 「次のラウンドへ」押下時に試合終了判定を行い、
 * - 試合終了: closeRoundResult → openGameOver
 * - 継続: closeRoundResult → startNextRound
 */
export const RoundResultModal = () => {
  const { modals, closeRoundResult, openGameOver } = useUIStore();
  const { gameState, matchState, matchManager, startNextRound } = useGameStore();

  const { isOpen, result } = modals.roundResult;

  if (!isOpen || !result || !gameState || !matchState) {
    return null;
  }

  const { players } = gameState;
  const { lifePoints } = matchState;

  // 勝利チーム
  const winnerTeam = result.winner;
  const winnerLabel = winnerTeam === Team.TEAM_1 ? 'Team 1' : 'Team 2';

  // 敗北チームのプレイヤー
  const losingTeam = winnerTeam === Team.TEAM_1 ? Team.TEAM_2 : Team.TEAM_1;
  const losingPlayers = players.filter(p => p.team === losingTeam);

  // 試合終了判定（matchManager 経由）
  const isMatchFinished = matchManager?.isMatchFinished() ?? false;
  const matchWinner = isMatchFinished ? matchManager?.getMatchWinner() ?? null : null;

  const handleNextRound = () => {
    closeRoundResult();
    if (isMatchFinished && matchWinner) {
      // 試合終了: GameOverModal を開く
      openGameOver(matchWinner);
    } else {
      // 継続: 次のラウンドへ
      startNextRound();
    }
  };

  // ボタンラベルを試合終了状態に応じて切り替え
  const buttonLabel = isMatchFinished ? '結果を見る' : '次のラウンドへ';

  return (
    <div className="round-result-overlay">
      <div className="round-result-modal">
        <div className="round-result-header">
          <h2>Round {result.roundNumber} 終了</h2>
          <div className={`winner-announcement ${winnerTeam === Team.TEAM_1 ? 'team-1' : 'team-2'}`}>
            {winnerLabel} の勝利!
          </div>
        </div>

        <div className="round-result-content">
          <div className="damage-section">
            <h3>ダメージ計算</h3>
            <div className="damage-list">
              {losingPlayers.map(player => {
                const damage = result.damageDealt[player.id] ?? 0;
                const remainingCards = result.remainingCards[player.id] ?? 0;
                const currentPoints = lifePoints[player.id] ?? 0;

                return (
                  <div key={player.id} className="damage-item">
                    <div className="damage-player-info">
                      <span className="damage-player-name">{player.name}</span>
                      <span className="damage-cards-info">
                        残り {remainingCards} 枚
                      </span>
                    </div>
                    <div className="damage-values">
                      <span className="damage-amount">-{damage}</span>
                      <span className="damage-arrow">→</span>
                      <span className={`damage-remaining ${currentPoints <= 0 ? 'defeated' : currentPoints <= 20 ? 'low' : ''}`}>
                        {currentPoints}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="current-standings">
            <h3>現在の持ち点</h3>
            <div className="standings-list">
              {players.map(player => {
                const points = lifePoints[player.id] ?? 0;
                const isTeam1 = player.team === Team.TEAM_1;

                return (
                  <div
                    key={player.id}
                    className={`standing-item ${isTeam1 ? 'team-1' : 'team-2'} ${points <= 0 ? 'defeated' : ''}`}
                  >
                    <span className="standing-name">{player.name}</span>
                    <span className="standing-points">{points}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="round-result-actions">
          <button className="next-round-button" onClick={handleNextRound}>
            {buttonLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoundResultModal;
